import { Button, Table, UncontrolledTooltip } from "reactstrap";
import { RiskPacket } from "./risk-packet/RiskPacket";
import {
  getHerdValue,
  HerdValueModel,
  HerdValueView,
  isHerdValueModel,
} from "../models/cow-herd-value.model";
import {
  HerdScenario,
  BestHerdScenario,
  WorstHerdScenario,
  isHerdScenario,
  HerdScenarioRange,
  CowClassifications,
} from "../models/cow-model-parameters.model";
import { Group } from "../models/group.model";
import { LabelledPieChart } from "./LabelledPieChart";
import { Dispatch, SetStateAction, useState } from "react";
import { ValueItemView } from "../models/value-item-view.model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  BestFlockScenario,
  FlockScenario,
  FlockScenarioRange,
  SheepClassifications,
  WorstFlockScenario,
} from "../models/sheep-model-parameters.model";
import {
  FlockValueModel,
  FlockValueView,
  getFlockValue,
} from "../models/sheep-flock-value.model";
import { ShowParametersModal } from "./ShowParametersModal";
import { DataModel, isModelValid } from "../models/data.model";
import { TargetFormTabbed } from "./TargetFormTabbed";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { RegisterCallToAction } from "./RegisterCallToAction";
import { RoundedCard } from "../layout/RoundedCard";
import classnames from "classnames";

interface Props {
  model: DataModel;
  setModel: Dispatch<SetStateAction<DataModel>>;
}

export const ValueForecast = ({ model, setModel }: Props) => {
  const [isBrief, setIsBrief] = useState(true);
  const toggleBrief = () => setIsBrief(!isBrief);

  const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);
  const toggleParamsModal = () => setIsParamsModalOpen(!isParamsModalOpen);

  if (
    model.group === undefined ||
    !isModelValid(model) ||
    model.scenario === undefined
  ) {
    return <></>;
  }

  const isCows = isHerdScenario(model.scenario);
  const group = model.group as Required<Group>;
  const scenarios = {
    worst: {
      scenario: isCows ? WorstHerdScenario : WorstFlockScenario,
      valueModel: isCows
        ? getHerdValue(group, WorstHerdScenario)
        : getFlockValue(group, WorstFlockScenario),
      label: "Worst",
      className: "table-danger",
    },
    target: {
      scenario: model.scenario,
      valueModel: isCows
        ? getHerdValue(group, model.scenario as HerdScenario)
        : getFlockValue(group, model.scenario as FlockScenario),
      label: `Own ${isCows ? "herd" : "flock"}`,
      className: "table-primary",
    },
    best: {
      scenario: isCows ? BestHerdScenario : BestFlockScenario,
      valueModel: isCows
        ? getHerdValue(group, BestHerdScenario)
        : getFlockValue(group, BestFlockScenario),
      label: "Best",
      className: "table-success",
    },
  };

  const targetModel = scenarios.target.valueModel;
  const isCowsValueModel = isHerdValueModel(targetModel);
  const pieChartData = isCowsValueModel
    ? [
        {
          name: "Adult cows losses",
          value: targetModel.cowMortality,
        },
        {
          name: "Calf Abortions",
          value: targetModel.calfAbortions,
        },
        { name: "Stillborns", value: targetModel.stillborns },
        {
          name: "First week losses",
          value: targetModel.firstWeekCalfLosses,
        },
        {
          name: "Losses after first week",
          value: targetModel.calfLossesAfterFirstWeek,
        },
      ]
    : [
        {
          name: "Ewe mortality",
          value: targetModel.eweMortality,
        },
        {
          name: "Lamb loss due to ewe death",
          value: targetModel.lambLossDueToEweDeath,
        },
        {
          name: "Lamb abortions",
          value: targetModel.abortedLambs,
        },
        {
          name: "Lamb mortality first 8 weeks",
          value: targetModel.lambMortalityTo8Weeks,
        },
        {
          name: "Lamb mortality 8 to 12 weeks",
          value: targetModel.lambMortality8WeeksToWeaning,
        },
      ];

  const juvenileLosses = isCowsValueModel
    ? targetModel.totalCalfLosses
    : targetModel.totalLambLosses;
  const juvenile = isCowsValueModel ? "calf" : "lamb";
  const juvenileLossesTitle = `Total ${juvenile} losses: ${juvenileLosses.toFixed(
    0
  )} Hd.`;

  const adultLosses = isCowsValueModel
    ? targetModel.cowMortality
    : targetModel.eweMortality;
  const adult = isCowsValueModel ? "cow" : "ewe";
  const adultLossesTitle = `Total ${adult} losses: ${adultLosses.toFixed(
    0
  )} Hd.`;

  const bestValue = isHerdValueModel(scenarios.best.valueModel)
    ? scenarios.best.valueModel.herdValue
    : scenarios.best.valueModel.flockValue;
  const worstValue = isHerdValueModel(scenarios.worst.valueModel)
    ? scenarios.worst.valueModel.herdValue
    : scenarios.worst.valueModel.flockValue;

  const infoIcon = (
    <FontAwesomeIcon icon={faInfoCircle} fixedWidth className="ms-1 hand" />
  );

  const isIncluded = (view: ValueItemView) =>
    view &&
    ((isBrief && view.includedInBriefView) ||
      (!isBrief && view.includedInVerboseView));

  const valueView = isCows ? HerdValueView : FlockValueView;

  const tableBody = Object.entries(valueView)
    .filter(([_, view]) => isIncluded(view))
    .map(([key, view]) => (
      <tr key={key}>
        <td
          id={key}
          style={{ justifyContent: "space-between", display: "flex" }}
          className={classnames({ "fw-bold": view.emphasized })}
        >
          {view.description}
          {view.toolTip && infoIcon}
          {view.toolTip && (
            <UncontrolledTooltip target={key} placement="bottom">
              {view.toolTip}
            </UncontrolledTooltip>
          )}
        </td>
        {Object.values(scenarios).map((s) => (
          <td
            style={{ textAlign: "right" }}
            className={classnames(s.className, { "fw-bold": view.emphasized })}
            key={`${s.label}-${key}`}
          >
            {view.format?.({
              value: s.valueModel[key as keyof typeof s.valueModel],
              model: s.valueModel,
            })}
          </td>
        ))}
      </tr>
    ));

  return (
    <ResponsiveMasonry columnsCountBreakPoints={{ 300: 1, 800: 2 }}>
      <ShowParametersModal
        isOpen={isParamsModalOpen}
        toggle={toggleParamsModal}
        header="Scenario Parameters"
        scenarios={Object.values(scenarios)}
        ranges={isCows ? HerdScenarioRange : FlockScenarioRange}
      />
      <Masonry>
        <RegisterCallToAction />
        <div></div>
        <RiskPacket
          bestValue={bestValue}
          worstValue={worstValue}
          farmValue={
            isCowsValueModel
              ? (scenarios.target.valueModel as HerdValueModel).herdValue
              : (scenarios.target.valueModel as FlockValueModel).flockValue
          }
          livestockGroup={isCows ? "herd" : "flock"}
        />
        <div></div>
        <RoundedCard className="masonry-item" id="forecast-details">
          <div>
            <span>
              {model.forecastName ? model.forecastName : "My forecast"}
            </span>
          </div>
          <div style={{ width: "calc(100% + 3em)", margin: "0 -1.5em" }}>
            <Table
              responsive
              style={{ fontSize: "small" }}
              className="table forecast-table"
            >
              <thead>
                <tr className="position-relative hand">
                  <th style={{ width: "25%" }}>
                    <Button
                      color="dark"
                      onClick={toggleBrief}
                      style={{ width: "100%" }}
                      outline
                    >
                      <FontAwesomeIcon
                        icon={isBrief ? faCaretDown : faCaretUp}
                        className="me-1"
                      />{" "}
                      <span>Details</span>
                    </Button>
                  </th>
                  {Object.values(scenarios).map((s) => (
                    <th
                      style={{ textAlign: "center", width: "25%" }}
                      className={s.className}
                      key={s.label}
                      onClick={toggleParamsModal}
                    >
                      {s.label}
                    </th>
                  ))}
                  <td
                    style={{
                      position: "absolute",
                      top: ".5em",
                      right: ".5em",
                      backgroundColor: "transparent",
                      pointerEvents: "none",
                      padding: 0,
                      borderBottomWidth: 0,
                    }}
                  >
                    {infoIcon}
                  </td>
                </tr>
              </thead>
              <tbody>{tableBody}</tbody>
            </Table>
          </div>
        </RoundedCard>
        <RoundedCard className="masonry-item" id="forecast-explorer">
          <span>{`Explore your own ${
            isCows ? "herd" : "flock"
          } forecast parameters`}</span>
          <TargetFormTabbed
            classifications={
              model.kind === "cows" ? CowClassifications : SheepClassifications
            }
            target={model.scenario as HerdScenario | FlockScenario}
            setTarget={(scenario: any) => setModel((m) => ({ ...m, scenario }))}
            ranges={isCows ? HerdScenarioRange : FlockScenarioRange}
            markerValues={
              model.kind === "cows"
                ? {
                    best: BestHerdScenario,
                    worst: WorstHerdScenario,
                    // TODO: re-instate reference markers if we have the data?
                    //reference: ReferenceHerdScenario,
                  }
                : {
                    best: BestFlockScenario,
                    worst: WorstFlockScenario,
                    // TODO: re-instate reference markers if we have the data?
                    //reference: ReferenceFlockScenario,
                  }
            }
          />
        </RoundedCard>
        <div></div>
        <RoundedCard className="masonry-item" id="piechart-card">
          <LabelledPieChart
            data={pieChartData}
            titles={[adultLossesTitle, juvenileLossesTitle]}
          />
        </RoundedCard>
      </Masonry>
    </ResponsiveMasonry>
  );
};
